import React, { useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import { Box, ButtonBase } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';

/**
 * One line of filter chips that scrolls sideways — and shows that it does.
 *
 * Vendors read a row with a hidden scrollbar as "that's all there is". So when chips are hidden
 * off an edge, a strip fades in over that edge with a small round arrow on it; tapping the arrow
 * slides the row along (for anyone who never thinks to swipe). At the start there is no left
 * arrow, at the end no right one, and a row that fits shows neither — short rows look as before.
 *
 * The strip is a separate layer painted in the colour behind the row (looked up once, so it
 * matches pages, cards and dialogs in light or dark mode): solid under the arrow, fading to
 * clear. It used to be a CSS mask on the scrolling row itself, which iPhone Safari draws wrongly
 * on scrolling elements — part of a row went blank and the arrows sat over readable chips.
 *
 * children  the chips (anything); they keep their own width
 * gap       MUI spacing between chips, default 0.75
 * px        padding inside the scroller, for a row that runs edge to edge (mx: -2 on sx)
 * sx        merged onto the outer box (spacing only)
 */
const EDGE = 44;   // width of the edge strip: room for the 40px arrow, then the fade

// The first real (non-transparent) background colour at or above `el`.
function backgroundBehind(el) {
    for (let node = el; node && node !== document.documentElement; node = node.parentElement) {
        const c = window.getComputedStyle(node).backgroundColor;
        if (c && c !== 'transparent' && !/rgba\([^)]*,\s*0\)$/.test(c)) return c;
    }
    return null;
}

export default function ScrollChipRow({ children, gap = 0.75, px = 0, sx = {} }) {
    const theme = useTheme();
    const outer = useRef(null);
    const ref = useRef(null);
    const [edges, setEdges] = useState({ left: false, right: false });
    const [bg, setBg] = useState(null);

    useLayoutEffect(() => {
        if (outer.current) setBg(backgroundBehind(outer.current.parentElement));
    }, [theme.palette.mode]);

    const measure = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        const left = el.scrollLeft > 2;
        const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
        setEdges(prev => (prev.left === left && prev.right === right ? prev : { left, right }));
    }, []);

    // Re-measure when the row scrolls, resizes (rotation, a dialog opening), or its chips change.
    useEffect(() => {
        const el = ref.current;
        if (!el) return undefined;
        measure();
        el.addEventListener('scroll', measure, { passive: true });
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            ro.observe(el);
            Array.from(el.children).forEach(c => ro.observe(c));
        } else {
            window.addEventListener('resize', measure);
        }
        return () => {
            el.removeEventListener('scroll', measure);
            if (ro) ro.disconnect(); else window.removeEventListener('resize', measure);
        };
    }, [measure, children]);

    const slide = (dir) => {
        const el = ref.current;
        if (!el) return;
        const by = dir * Math.max(120, el.clientWidth * 0.7);
        const target = Math.max(0, Math.min(el.scrollWidth - el.clientWidth, el.scrollLeft + by));
        try { el.scrollTo({ left: target, behavior: 'smooth' }); } catch { el.scrollLeft = target; }
    };

    const base = bg || theme.palette.background.paper;
    const edge = (dir) => {
        const show = dir > 0 ? edges.right : edges.left;
        return (
            <Box sx={{
                position: 'absolute', top: 0, bottom: 0, width: EDGE, zIndex: 1,
                [dir > 0 ? 'right' : 'left']: 0,
                display: 'flex', alignItems: 'center', justifyContent: dir > 0 ? 'flex-end' : 'flex-start',
                // solid under the arrow, then clear towards the chips
                background: `linear-gradient(to ${dir > 0 ? 'left' : 'right'}, ${base} 60%, transparent)`,
                opacity: show ? 1 : 0, pointerEvents: 'none', transition: 'opacity .15s',
            }}>
                <ButtonBase
                    aria-label={dir > 0 ? 'More filters' : 'Back'}
                    onClick={() => slide(dir)}
                    tabIndex={show ? 0 : -1}
                    sx={{
                        width: 40, height: 40, borderRadius: '50%', flexShrink: 0,
                        pointerEvents: show ? 'auto' : 'none',
                        '& .chev': {
                            width: 26, height: 26, borderRadius: '50%', display: 'flex',
                            alignItems: 'center', justifyContent: 'center',
                            bgcolor: 'background.paper', color: 'primary.main',
                            border: '1px solid', borderColor: 'divider', boxShadow: 1,
                        },
                    }}>
                    <Box className="chev">
                        {dir > 0 ? <ChevronRight sx={{ fontSize: 18 }} /> : <ChevronLeft sx={{ fontSize: 18 }} />}
                    </Box>
                </ButtonBase>
            </Box>
        );
    };

    return (
        <Box ref={outer} sx={{ position: 'relative', ...sx }}>
            <Box ref={ref} sx={{
                display: 'flex', flexWrap: 'nowrap', gap, alignItems: 'center', overflowX: 'auto', px,
                '& > *': { flexShrink: 0 },
                '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
            }}>
                {children}
            </Box>
            {edge(-1)}
            {edge(1)}
        </Box>
    );
}
